// hooks/useSegmentState.ts - Segment state management
import { useCallback, useReducer } from 'react';
import type { SetStateAction } from 'react';
import { ComboEdge, Segment, SegmentState } from '../types';
import {
  createComboFromSourceIds,
  DEFAULT_SEGMENT_OVERLAP_SECONDS,
  duplicateComboSegment,
  getSourceSegments,
  isCombo,
  mergeLearnedSegments,
  normalizeCombos,
  removeSegmentAndRepairCombos,
  resolveActiveComboId,
  resizeComboToEdgeTime
} from '../utils';

const MAX_SEGMENT_UNDO_DEPTH = 40;

const initialSegmentState: SegmentState = {
  segments: [],
  activeSegment: null,
  activeComboId: null,
  isMarkingSegment: false,
  currentSegmentStart: null,
  isLooping: false,
  transitionType: "none",
  transitionDuration: 0.5,
  segmentOverlap: DEFAULT_SEGMENT_OVERLAP_SECONDS
};

type SegmentStateUpdate = SetStateAction<SegmentState>;

type SegmentHistoryState = {
  present: SegmentState;
  past: SegmentState[];
};

type SegmentHistoryAction =
  | { type: 'set'; update: SegmentStateUpdate; recordHistory: boolean }
  | { type: 'undo' }
  | { type: 'clearHistory' };

const cloneSegment = (segment: Segment): Segment => ({
  ...segment,
  ...(segment.sourceSegmentIds ? { sourceSegmentIds: [...segment.sourceSegmentIds] } : {})
});

const cloneSegmentState = (state: SegmentState): SegmentState => ({
  ...state,
  segments: state.segments.map(cloneSegment)
});

const resolveSegmentStateUpdate = (
  update: SegmentStateUpdate,
  previous: SegmentState
): SegmentState => (
  typeof update === 'function'
    ? (update as (prev: SegmentState) => SegmentState)(previous)
    : update
);

const segmentHistoryReducer = (
  state: SegmentHistoryState,
  action: SegmentHistoryAction
): SegmentHistoryState => {
  if (action.type === 'undo') {
    const previous = state.past[state.past.length - 1];
    if (!previous) return state;

    return {
      present: previous,
      past: state.past.slice(0, -1)
    };
  }

  if (action.type === 'clearHistory') {
    if (state.past.length === 0) return state;
    return {
      ...state,
      past: []
    };
  }

  const next = resolveSegmentStateUpdate(action.update, state.present);
  if (next === state.present) return state;

  return {
    present: next,
    past: action.recordHistory
      ? [...state.past.slice(-(MAX_SEGMENT_UNDO_DEPTH - 1)), cloneSegmentState(state.present)]
      : state.past
  };
};

export const useSegmentState = () => {
  const [historyState, dispatchSegmentState] = useReducer(segmentHistoryReducer, {
    present: initialSegmentState,
    past: []
  });
  const segmentState = historyState.present;

  const setSegmentState = useCallback((update: SegmentStateUpdate) => {
    dispatchSegmentState({ type: 'set', update, recordHistory: false });
  }, []);

  const commitSegmentState = useCallback((update: SegmentStateUpdate) => {
    dispatchSegmentState({ type: 'set', update, recordHistory: true });
  }, []);

  const undoLastSegmentChange = useCallback(() => {
    dispatchSegmentState({ type: 'undo' });
  }, []);

  const clearSegmentUndoHistory = useCallback(() => {
    dispatchSegmentState({ type: 'clearHistory' });
  }, []);

  // All segment functions here
  const toggleLoop = () => {
    setSegmentState(prev => ({ ...prev, isLooping: !prev.isLooping }));
  };

  const deleteSegment = (segmentId: string) => {
    commitSegmentState(prev => {
      const nextSegments = removeSegmentAndRepairCombos(prev.segments, segmentId);
      const requestedActiveSegment = prev.activeSegment === segmentId ? null : prev.activeSegment;
      const nextActiveSegment = requestedActiveSegment &&
        nextSegments.some(segment => segment.id === requestedActiveSegment)
          ? requestedActiveSegment
          : null;

      return {
        ...prev,
        segments: nextSegments,
        activeSegment: nextActiveSegment,
        activeComboId: resolveActiveComboId(nextSegments, nextActiveSegment, prev.activeComboId),
        isLooping: nextActiveSegment ? prev.isLooping : false
      };
    });
  };

  const saveSegmentName = (segmentId: string, newName: string) => {
    commitSegmentState(prev => {
      const target = prev.segments.find(segment => segment.id === segmentId);
      if (!target) return prev;

      const targetIsCombo = isCombo(target);
      const nameAlreadyEdited = targetIsCombo
        ? target.comboNameEdited
        : target.segmentNameEdited;
      if (target.name === newName && nameAlreadyEdited) return prev;

      return {
        ...prev,
        segments: prev.segments.map(segment =>
          segment.id === segmentId
            ? {
                ...segment,
                name: newName,
                ...(isCombo(segment) ? { comboNameEdited: true } : { segmentNameEdited: true })
              }
            : segment
        )
      };
    });
  };

  const updateSegmentTransition = (segmentId: string, duration: number) => {
    commitSegmentState(prev => {
      const target = prev.segments.find(segment => segment.id === segmentId);
      if (!target || target.transitionDuration === duration) return prev;

      return {
        ...prev,
        segments: prev.segments.map(segment =>
          segment.id === segmentId ? { ...segment, transitionDuration: duration } : segment
        )
      };
    });
  };

  const toggleSegmentLearned = (segmentId: string) => {
    setSegmentState(prev => ({
      ...prev,
      segments: prev.segments.map(segment => 
        segment.id === segmentId 
          ? { ...segment, isLearned: !segment.isLearned } 
          : segment
      )
    }));
  };

  const handleMergeLearnedSegments = () => {
    const mergedSegment = mergeLearnedSegments(segmentState.segments, segmentState.transitionDuration);
    
    if (!mergedSegment) {
      alert("Please mark at least two segments as learned before merging.");
      return;
    }

    const mergedSourceIds = new Set(
      segmentState.segments
        .filter(segment => segment.isLearned)
        .map(segment => segment.id)
    );
    
    commitSegmentState(prev => ({
      ...prev,
      segments: [
        ...prev.segments.map(segment =>
          mergedSourceIds.has(segment.id)
            ? { ...segment, isLearned: false }
            : segment
        ),
        mergedSegment
      ],
      activeSegment: mergedSegment.id,
      activeComboId: mergedSegment.id
    }));
  };

  const createComboFromActiveSegment = () => {
    const sourceSegments = getSourceSegments(segmentState.segments);
    if (sourceSegments.length < 2) {
      alert('Create at least two segments before building a combo.');
      return false;
    }

    const activeSource = segmentState.activeSegment
      ? sourceSegments.find(segment => segment.id === segmentState.activeSegment)
      : null;
    const sourceSegment = activeSource ?? sourceSegments[0];
    const sourceIndex = sourceSegment
      ? sourceSegments.findIndex(segment => segment.id === sourceSegment.id)
      : -1;

    if (!sourceSegment) {
      alert('Create at least one segment before building a combo.');
      return false;
    }

    const edgeSegment = sourceIndex > 0
      ? sourceSegments[sourceIndex - 1]
      : sourceSegments[sourceIndex + 1];
    const defaultSourceIds = [edgeSegment.id, sourceSegment.id];

    const combo = createComboFromSourceIds(
      segmentState.segments,
      defaultSourceIds,
      segmentState.transitionDuration,
      `combo-${Date.now()}`,
      sourceSegment.id,
      edgeSegment.id
    );

    if (!combo) return false;

    commitSegmentState(prev => ({
      ...prev,
      segments: normalizeCombos([...prev.segments, combo]),
      activeSegment: combo.id,
      activeComboId: combo.id,
      isLooping: true
    }));
    return true;
  };

  /**
   * The source-segment range a directional combo would cover: the playing segment (or the playing
   * combo's range) plus one neighbouring segment on the chosen side. Null when there is no
   * neighbour on that side.
   */
  const getComboExtensionIds = (direction: 'left' | 'right'): string[] | null => {
    const sourceSegments = getSourceSegments(segmentState.segments);
    if (sourceSegments.length < 2) return null;

    const active = segmentState.activeSegment
      ? segmentState.segments.find(segment => segment.id === segmentState.activeSegment) ?? null
      : null;
    const baseIds = active && isCombo(active)
      ? active.sourceSegmentIds ?? []
      : [active?.id ?? sourceSegments[0].id];
    const baseIndices = baseIds
      .map(id => sourceSegments.findIndex(segment => segment.id === id))
      .filter(index => index >= 0);
    if (baseIndices.length === 0) return null;

    const first = Math.min(...baseIndices);
    const last = Math.max(...baseIndices);
    const neighbourIndex = direction === 'left' ? first - 1 : last + 1;
    if (neighbourIndex < 0 || neighbourIndex >= sourceSegments.length) return null;

    return sourceSegments
      .slice(Math.min(first, neighbourIndex), Math.max(last, neighbourIndex) + 1)
      .map(segment => segment.id);
  };

  /**
   * Combo left/right: pairs the playing segment with its neighbour on that side, or extends the
   * playing combo by one segment on that side. Reuses an identical existing combo instead of
   * creating a duplicate. The result is selected and looped.
   */
  const extendComboFromActive = (direction: 'left' | 'right'): boolean => {
    const sourceIds = getComboExtensionIds(direction);
    if (!sourceIds) return false;

    const existing = segmentState.segments.find(segment => (
      isCombo(segment) &&
      !segment.isDisabled &&
      (segment.sourceSegmentIds ?? []).length === sourceIds.length &&
      (segment.sourceSegmentIds ?? []).every((id, index) => id === sourceIds[index])
    ));
    if (existing) {
      setSegmentState(prev => ({
        ...prev,
        activeSegment: existing.id,
        activeComboId: existing.id,
        isLooping: true
      }));
      return true;
    }

    const active = segmentState.activeSegment
      ? segmentState.segments.find(segment => segment.id === segmentState.activeSegment) ?? null
      : null;
    const neighbourId = direction === 'left' ? sourceIds[0] : sourceIds[sourceIds.length - 1];
    const originId = active && isCombo(active)
      ? active.originSegmentId ?? sourceIds[0]
      : active?.id ?? (direction === 'left' ? sourceIds[sourceIds.length - 1] : sourceIds[0]);

    const combo = createComboFromSourceIds(
      segmentState.segments,
      sourceIds,
      segmentState.transitionDuration,
      `combo-${Date.now()}`,
      originId,
      neighbourId
    );
    if (!combo) return false;

    commitSegmentState(prev => ({
      ...prev,
      segments: normalizeCombos([...prev.segments, combo]),
      activeSegment: combo.id,
      activeComboId: combo.id,
      isLooping: true
    }));
    return true;
  };

  const selectCombo = (comboId: string) => {
    setSegmentState(prev => {
      const combo = prev.segments.find(segment => segment.id === comboId && isCombo(segment));
      if (!combo || combo.isDisabled) return prev;

      return {
        ...prev,
        activeSegment: comboId,
        activeComboId: comboId,
        isLooping: true
      };
    });
  };

  const duplicateCombo = (comboId?: string) => {
    const sourceComboId = comboId ?? segmentState.activeComboId ?? segmentState.activeSegment;
    if (!sourceComboId) return false;

    const normalizedDuplicate = duplicateComboSegment(
      segmentState.segments,
      sourceComboId,
      `combo-${Date.now()}`
    );
    if (!normalizedDuplicate) return false;

    commitSegmentState(prev => ({
      ...prev,
      segments: [...prev.segments, normalizedDuplicate],
      activeSegment: normalizedDuplicate.id,
      activeComboId: normalizedDuplicate.id,
      isLooping: true
    }));
    return true;
  };

  const deleteCombo = (comboId?: string) => {
    commitSegmentState(prev => {
      const targetComboId = comboId ?? prev.activeComboId ?? prev.activeSegment;
      if (!targetComboId) return prev;

      const nextSegments = prev.segments.filter(segment => segment.id !== targetComboId);
      const nextActiveSegment = prev.activeSegment === targetComboId ? null : prev.activeSegment;

      return {
        ...prev,
        segments: nextSegments,
        activeSegment: nextActiveSegment,
        activeComboId: resolveActiveComboId(nextSegments, nextActiveSegment, prev.activeComboId),
        isLooping: prev.activeSegment === targetComboId ? false : prev.isLooping
      };
    });
  };

  const resizeComboEdge = (comboId: string, edge: ComboEdge, time: number) => {
    commitSegmentState(prev => {
      const nextSegments = normalizeCombos(prev.segments.flatMap(segment => {
        if (segment.id !== comboId || !isCombo(segment)) return [segment];

        const resizedCombo = resizeComboToEdgeTime(prev.segments, segment, edge, time);
        return resizedCombo ? [resizedCombo] : [];
      }));
      const comboStillExists = nextSegments.some(segment => segment.id === comboId && isCombo(segment));
      const nextActiveSegment = !comboStillExists && prev.activeSegment === comboId
        ? null
        : prev.activeSegment;

      return {
        ...prev,
        segments: nextSegments,
        activeComboId: resolveActiveComboId(nextSegments, nextActiveSegment, comboStillExists ? comboId : prev.activeComboId),
        activeSegment: nextActiveSegment,
        isLooping: nextActiveSegment ? prev.isLooping : false
      };
    });
  };

  const handleTransitionTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSegmentState(prev => ({ ...prev, transitionType: event.target.value }));
  };

  const handleTransitionDurationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const duration = parseFloat(event.target.value);
    setSegmentState(prev => ({ ...prev, transitionDuration: duration }));
  };

  // Helper function to set active segment
  const setActiveSegment = (segmentId: string | null) => {
    setSegmentState(prev => {
      return {
        ...prev,
        activeSegment: segmentId,
        activeComboId: resolveActiveComboId(prev.segments, segmentId, prev.activeComboId)
      };
    });
  };

  // Helper function to play a segment (sets it as active and looping)
  // `loop` defaults to on; edit mode passes the user's remembered edit-mode loop choice instead.
  const activateSegmentForPlay = (segmentId: string, loop = true) => {
    setSegmentState(prev => {
      return {
        ...prev,
        activeSegment: segmentId,
        activeComboId: resolveActiveComboId(prev.segments, segmentId, prev.activeComboId),
        isLooping: loop
      };
    });
  };

  // Return everything the component needs
  return {
    segmentState,
    setSegmentState,
    commitSegmentState,
    canUndoSegmentChange: historyState.past.length > 0,
    undoLastSegmentChange,
    clearSegmentUndoHistory,
    toggleLoop,
    deleteSegment,
    saveSegmentName,
    updateSegmentTransition,
    toggleSegmentLearned,
    handleMergeLearnedSegments,
    createComboFromActiveSegment,
    getComboExtensionIds,
    extendComboFromActive,
    selectCombo,
    duplicateCombo,
    deleteCombo,
    resizeComboEdge,
    handleTransitionTypeChange,
    handleTransitionDurationChange,
    setActiveSegment,
    activateSegmentForPlay
  };
};
